import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Truck, Printer, XCircle, MapPin, Loader2 } from 'lucide-react';
import { brandName, brandEmail, brandPhone } from '../lib/brand';

const inputStyle = {
  width: '100%', padding: '7px 9px', border: '1px solid #E2E8F0', borderRadius: 6,
  fontSize: 13, color: '#0F172A', background: '#fff', boxSizing: 'border-box',
};
const labelStyle = { fontSize: 10, fontWeight: 700, color: '#94A3B8', letterSpacing: '0.05em', textTransform: 'uppercase', display: 'block', marginBottom: 3 };
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '');

const AddressFields = ({ addr, setAddr, prefix }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
    <div style={{ gridColumn: '1 / -1' }}>
      <label style={labelStyle}>Name</label>
      <input style={inputStyle} value={addr.name} onChange={(e) => setAddr({ ...addr, name: e.target.value })} data-testid={`${prefix}-name`} />
    </div>
    <div style={{ gridColumn: '1 / -1' }}>
      <label style={labelStyle}>Street</label>
      <input style={inputStyle} value={addr.street1} onChange={(e) => setAddr({ ...addr, street1: e.target.value })} data-testid={`${prefix}-street1`} />
    </div>
    <div>
      <label style={labelStyle}>City</label>
      <input style={inputStyle} value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} data-testid={`${prefix}-city`} />
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 8 }}>
      <div>
        <label style={labelStyle}>State</label>
        <input style={inputStyle} value={addr.state} onChange={(e) => setAddr({ ...addr, state: e.target.value.toUpperCase() })} maxLength={2} data-testid={`${prefix}-state`} />
      </div>
      <div>
        <label style={labelStyle}>ZIP</label>
        <input style={inputStyle} value={addr.zip} onChange={(e) => setAddr({ ...addr, zip: e.target.value })} data-testid={`${prefix}-zip`} />
      </div>
    </div>
    <div style={{ gridColumn: '1 / -1' }}>
      <label style={labelStyle}>Phone</label>
      <input style={inputStyle} value={addr.phone || ''} onChange={(e) => setAddr({ ...addr, phone: e.target.value })} data-testid={`${prefix}-phone`} />
    </div>
  </div>
);

// Shipping Details panel — Shippo rate shopping + real label purchase.
export const ShippingPanel = ({ proposal, onUpdated }) => {
  const shipping = proposal?.shipping;
  const purchased = shipping?.status === 'label_purchased';
  const [configured, setConfigured] = useState(true);
  const [origin, setOrigin] = useState(null);
  const [showOrigin, setShowOrigin] = useState(false);
  const [originDraft, setOriginDraft] = useState({ name: brandName(), street1: '', city: '', state: 'FL', zip: '', phone: '' });
  const [addrTo, setAddrTo] = useState({
    name: proposal?.customer_name || '',
    street1: shipping?.address_to?.street1 || '',
    city: shipping?.address_to?.city || '',
    state: shipping?.address_to?.state || 'FL',
    zip: shipping?.address_to?.zip || '',
    phone: proposal?.customer_phone || '',
    email: proposal?.customer_email || '',
  });
  const [parcel, setParcel] = useState({ weight: '', length: '12', width: '10', height: '4' });
  const [rates, setRates] = useState(null);
  const [selectedRate, setSelectedRate] = useState(null);
  const [chargeCustomer, setChargeCustomer] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get('/shipping/origin').then((r) => {
      setConfigured(!!r.data.shippo_configured);
      if (r.data.origin) { setOrigin(r.data.origin); setOriginDraft(r.data.origin); }
    }).catch(() => {});
  }, []);

  const saveOrigin = async () => {
    try {
      const r = await axios.put('/shipping/origin', originDraft);
      setOrigin(r.data.origin);
      setShowOrigin(false);
      toast.success('Ship-From address saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
  };

  const getRates = async () => {
    if (!addrTo.name || !addrTo.street1 || !addrTo.city || !addrTo.state || !addrTo.zip) {
      toast.error('Fill in the full ship-to address'); return;
    }
    const w = parseFloat(parcel.weight);
    if (!Number.isFinite(w) || w <= 0) { toast.error('Enter the package weight (lb)'); return; }
    setBusy(true); setRates(null); setSelectedRate(null);
    try {
      const r = await axios.post(`/proposals/${proposal.id}/shipping/rates`, {
        address_to: { ...addrTo, country: 'US' },
        parcel: { weight: w, length: parseFloat(parcel.length) || 12, width: parseFloat(parcel.width) || 10, height: parseFloat(parcel.height) || 4 },
      });
      setRates(r.data.rates || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to fetch rates');
    } finally { setBusy(false); }
  };

  const buyLabel = async () => {
    const rate = rates?.find((r) => r.rate_id === selectedRate);
    if (!rate) return;
    const chargeNote = chargeCustomer ? `\n$${rate.amount} will be added to the customer's balance.` : '';
    if (!window.confirm(`Buy ${rate.carrier} ${rate.service} label for $${rate.amount}?${chargeNote}\n\nThe customer will be emailed the tracking number.`)) return;
    setBusy(true);
    try {
      const r = await axios.post(`/proposals/${proposal.id}/shipping/buy`, {
        rate_id: rate.rate_id, carrier: rate.carrier, service: rate.service,
        amount: rate.amount, est_days: rate.est_days, charge_customer: chargeCustomer,
      });
      toast.success(r.data.email_sent ? 'Label purchased · Tracking emailed to customer' : 'Label purchased');
      setRates(null);
      onUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Label purchase failed');
    } finally { setBusy(false); }
  };

  const voidLabel = async () => {
    if (!window.confirm('Void this shipping label? Shippo will refund the label cost (for unused labels).')) return;
    setBusy(true);
    try {
      await axios.post(`/proposals/${proposal.id}/shipping/void`);
      toast.success('Label voided — refund requested');
      onUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Void failed');
    } finally { setBusy(false); }
  };

  if (purchased) {
    const a = shipping.address_to || {};
    return (
      <div data-testid="shipping-panel-purchased">
        <div style={{ fontSize: 14, fontWeight: 700, color: '#0F172A', textAlign: 'center', marginBottom: 8 }}>
          {shipping.carrier} — {shipping.service}
        </div>
        {shipping.est_days != null && (
          <div style={{ fontSize: 12, color: '#16A34A', textAlign: 'center', marginBottom: 10 }}>
            Est. {shipping.est_days} day{shipping.est_days === 1 ? '' : 's'} in transit
          </div>
        )}
        <div style={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', letterSpacing: '0.05em', marginBottom: 2 }}>SHIP TO</div>
        <div style={{ fontSize: 12, color: '#0F172A', lineHeight: 1.6, textTransform: 'uppercase', marginBottom: 10 }}>
          {a.name}<br />{a.street1}<br />{a.city}, {a.state} {a.zip}{a.phone ? <><br />{a.phone}</> : null}
        </div>
        <div style={{ background: '#F8FAFC', borderRadius: 6, padding: 10, marginBottom: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', letterSpacing: '0.05em' }}>
            LABEL PURCHASED <span style={{ fontWeight: 500 }}>{fmtDate(shipping.purchased_at)}</span>
          </div>
          <div style={{ fontSize: 11, color: '#64748B', marginTop: 6 }}>Tracking Number</div>
          <a
            href={shipping.tracking_url || '#'} target="_blank" rel="noreferrer"
            style={{ fontSize: 13, color: '#2563EB', fontWeight: 600, wordBreak: 'break-all' }}
            data-testid="shipping-tracking-link"
          >
            {shipping.tracking_number}
          </a>
          {shipping.amount && <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 4 }}>Label cost: ${shipping.amount}</div>}
          {Number(shipping.charged_to_customer) > 0 && (
            <div style={{ fontSize: 11, color: '#16A34A', marginTop: 2 }} data-testid="charged-to-customer-note">
              ✓ ${Number(shipping.charged_to_customer).toFixed(2)} added to customer balance
            </div>
          )}
        </div>
        <button
          onClick={() => window.open(shipping.label_url, '_blank', 'noopener')}
          data-testid="print-shipping-label-btn"
          style={{ width: '100%', background: '#fff', border: '1px solid #E2E8F0', color: '#0F172A', padding: '9px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginBottom: 8 }}
        >
          <Printer size={13} /> Print Shipping Label
        </button>
        <button
          onClick={voidLabel}
          disabled={busy}
          data-testid="void-shipping-label-btn"
          style={{ width: '100%', background: '#fff', border: '1px solid #FECACA', color: '#DC2626', padding: '9px 12px', borderRadius: 6, cursor: busy ? 'wait' : 'pointer', fontSize: 12, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
        >
          <XCircle size={13} /> Void Shipping Label
        </button>
      </div>
    );
  }

  return (
    <div data-testid="shipping-panel">
      {shipping?.status === 'voided' && (
        <div style={{ fontSize: 11, color: '#DC2626', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 6, padding: '6px 8px', marginBottom: 10 }}>
          Previous label voided ({fmtDate(shipping.voided_at)}) — you can purchase a new one.
        </div>
      )}
      {!configured && (
        <div style={{ fontSize: 12, color: '#B45309', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 6, padding: '8px 10px', marginBottom: 10 }}>
          Shippo API key not configured — add SHIPPO_API_KEY to enable label purchase.
        </div>
      )}
      <div style={{ marginBottom: 12 }}>
        <AddressFields addr={addrTo} setAddr={setAddrTo} prefix="shipto" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 6, marginBottom: 12 }}>
        <div>
          <label style={labelStyle}>Wt (lb)</label>
          <input style={inputStyle} value={parcel.weight} onChange={(e) => setParcel({ ...parcel, weight: e.target.value })} placeholder="5" data-testid="parcel-weight" />
        </div>
        <div>
          <label style={labelStyle}>L (in)</label>
          <input style={inputStyle} value={parcel.length} onChange={(e) => setParcel({ ...parcel, length: e.target.value })} data-testid="parcel-length" />
        </div>
        <div>
          <label style={labelStyle}>W (in)</label>
          <input style={inputStyle} value={parcel.width} onChange={(e) => setParcel({ ...parcel, width: e.target.value })} data-testid="parcel-width" />
        </div>
        <div>
          <label style={labelStyle}>H (in)</label>
          <input style={inputStyle} value={parcel.height} onChange={(e) => setParcel({ ...parcel, height: e.target.value })} data-testid="parcel-height" />
        </div>
      </div>
      <button
        onClick={getRates}
        disabled={busy || !configured}
        data-testid="get-shipping-rates-btn"
        style={{ width: '100%', background: '#2563EB', color: '#fff', border: 'none', padding: '9px 12px', borderRadius: 6, cursor: busy ? 'wait' : 'pointer', fontSize: 12, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, opacity: configured ? 1 : 0.5 }}
      >
        {busy && !rates ? <Loader2 size={13} className="animate-spin" /> : <Truck size={13} />} Get Shipping Rates
      </button>

      {rates && rates.length > 0 && (
        <div style={{ marginTop: 10 }} data-testid="shipping-rates-list">
          {rates.map((r) => (
            <label key={r.rate_id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', border: `1px solid ${selectedRate === r.rate_id ? '#2563EB' : '#E2E8F0'}`, borderRadius: 6, marginBottom: 6, cursor: 'pointer', background: selectedRate === r.rate_id ? '#EFF6FF' : '#fff' }}>
              <input type="radio" name="shiprate" checked={selectedRate === r.rate_id} onChange={() => setSelectedRate(r.rate_id)} data-testid={`rate-option-${r.rate_id}`} />
              <span style={{ flex: 1 }}>
                <span style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#0F172A' }}>{r.carrier} — {r.service}</span>
                <span style={{ fontSize: 11, color: '#64748B' }}>{r.est_days != null ? `~${r.est_days} days` : r.duration_terms || ''}</span>
              </span>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#0F172A' }}>${r.amount}</span>
            </label>
          ))}
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#0F172A', margin: '8px 0 4px', cursor: 'pointer' }}>
            <input type="checkbox" checked={chargeCustomer} onChange={(e) => setChargeCustomer(e.target.checked)} data-testid="charge-customer-checkbox" />
            Add label cost to customer balance
          </label>
          <button
            onClick={buyLabel}
            disabled={!selectedRate || busy}
            data-testid="buy-shipping-label-btn"
            style={{ width: '100%', background: selectedRate ? '#10B981' : '#CBD5E1', color: '#fff', border: 'none', padding: '10px 12px', borderRadius: 6, cursor: selectedRate && !busy ? 'pointer' : 'not-allowed', fontSize: 12, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', marginTop: 4 }}
          >
            {busy ? 'Purchasing…' : 'Buy Label & Email Tracking'}
          </button>
        </div>
      )}

      <button
        onClick={() => setShowOrigin((v) => !v)}
        data-testid="toggle-shipfrom-btn"
        style={{ marginTop: 10, background: 'transparent', border: 'none', padding: 0, color: '#2563EB', fontSize: 12, cursor: 'pointer', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4 }}
      >
        <MapPin size={12} /> {origin ? `Ship-From: ${origin.city}, ${origin.state}` : 'Set Ship-From address'}
      </button>
      {showOrigin && (
        <div style={{ marginTop: 8, padding: 10, background: '#F8FAFC', borderRadius: 6 }}>
          <AddressFields addr={originDraft} setAddr={setOriginDraft} prefix="shipfrom" />
          <button
            onClick={saveOrigin}
            data-testid="save-shipfrom-btn"
            style={{ marginTop: 8, width: '100%', background: '#0F172A', color: '#fff', border: 'none', padding: '8px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
          >
            Save Ship-From
          </button>
        </div>
      )}
    </div>
  );
};

export default ShippingPanel;
